import { isMobile } from 'react-device-detect';
import EventCountdown from 'components/EventCountdown/EventCountdown';
import localStorageService from 'helpers/localStorageService';
import useModalState from 'hooks/useModalState';
import moment from 'moment';
import { useMappedState } from 'redux-react-hook';
import styled from 'styled-components';

import mask from '../../../assets/mask.svg';
import StripeOfferBannerModal from '../../../modals/StripeOfferBannerModal';
import { formatDateToFullFormatWithSeconds, FULL_DATE_FORMAT_WITH_SECONDS, now } from '../../../utils/dates';

interface IImageProp {
  src: string,
}

interface IRootProps {
  children?: React.ReactNode;
  onClick: () => void;
}


export const StripeOfferBanner: React.FC = () => {

  const [isOpenStripeBannerModal, openStripeBannerModal, closeStripeBannerModal] = useModalState();

  const { currentUser } = useMappedState(
    state => {
      const currentUserId = state.users.currentUserId;
      const currentUser = state.users.usersMap.get(currentUserId);
      return {
        currentUser
      };
    }
  );

  const subscriptionEndDate = formatDateToFullFormatWithSeconds(currentUser?.subscription?.currentPeriodEndDate ?? '');

  const expireSubscriptionDate = moment(subscriptionEndDate, FULL_DATE_FORMAT_WITH_SECONDS);
  const nowTime = now();

  const isExpiredSub = nowTime.isAfter(expireSubscriptionDate);

  const expiredSubDateLsKey = localStorageService.get('isExpiredSubDate');
  const expiredSubDatePlusThreeDays = moment(expiredSubDateLsKey, FULL_DATE_FORMAT_WITH_SECONDS).add(3, 'days').format(FULL_DATE_FORMAT_WITH_SECONDS);

  const eventEndDate = isExpiredSub ? expiredSubDatePlusThreeDays : subscriptionEndDate;



  const desktopBanner = () => {
    return (
      <Banner onClick={openStripeBannerModal}>
        <CommonContainer>
          <MaskImage src={mask} />
          <CenterContainer>
            <DesktopCenterText>
              <Title>REMINDER: RENEW YOUR SUBSCRIPTION TO AVOID SERVICE INTERRUPTIONS</Title>
              <EventCountdown eventDate={eventEndDate} />
              <OfferDescription>Renew now 90% OFF</OfferDescription>
            </DesktopCenterText>
          </CenterContainer>
          <MaskImage src={mask} />
        </CommonContainer>
      </Banner>
    );
  };

  const mobileBanner = () => {
    return (
      <Banner onClick={openStripeBannerModal}>
        <MobileCommonContainer>
          <MobileText>
            <Title>REMINDER:</Title>
            <MobileSubtitle>RENEW YOUR SUBSCRIPTION</MobileSubtitle>
          </MobileText>
          <EventCountdown eventDate={eventEndDate} isMobile />
        </MobileCommonContainer>
      </Banner>
    );
  };

  return (
    <>

      {isMobile ? mobileBanner() : desktopBanner()}
      {isOpenStripeBannerModal && <StripeOfferBannerModal isOpen={isOpenStripeBannerModal} onClose={closeStripeBannerModal} analyticsDataRenderReason='stripe_offer_header_banner_click' />}
    </>
  );
};

const Banner = styled.div<IRootProps>`
  max-height: 60px;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: black;
  z-index: 100;
  cursor: pointer;
`;

const MaskImage = styled.img<IImageProp>`
  object-fit: cover;
  padding: 12px 0;
  opacity: 10%;
`;

const CommonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MobileCommonContainer = styled(CommonContainer)`
  justify-content: space-between;
  padding: 9px 24px;
`;

const OfferDescription = styled.div`
  height: 30px;
  padding: 6px 12px 6px 12px;
  border-radius: 12px;
  border: 1px solid #818181;
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 700;
  line-height: 18px;
  color: #FFFFFF; 

  @media (max-width: 820px) {
    height: auto;
  }
`;

const CenterContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: center;

  @media (max-width: 470px) {
    width: 100px;
    position: relative;
    left: -30px;
    margin: 0;
  }
`;

const DesktopCenterText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 0 20px;
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 20px;
`;

const MobileText = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 800;
  line-height: 28px;
  text-align: left;
  color: #ffffff;
`;

const MobileSubtitle = styled.p`
  font-family: Nunito Sans;
  font-size: 10px;
  font-weight: 700;
  line-height: 13px;
  text-align: left;
  color: #ffffff;
`;
